import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import usFlag from '@/assets/images/flags/us.svg';
import vnFlag from '@/assets/images/flags/vn.svg';
import type { AppLanguage } from '@/i18n';

interface LanguageOption {
  code: AppLanguage;
  flag: string;
  labelKey: 'language.english' | 'language.vietnamese';
}

const LANGUAGE_OPTIONS: LanguageOption[] = [
  { code: 'vi', flag: vnFlag, labelKey: 'language.vietnamese' },
  { code: 'en', flag: usFlag, labelKey: 'language.english' },
];

const AdminLanguageSwitcher: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const currentLanguage: AppLanguage = i18n.resolvedLanguage?.startsWith('en') ? 'en' : 'vi';
  const currentLanguageLabel = t(
    currentLanguage === 'vi' ? 'language.vietnamese' : 'language.english',
  );

  useEffect(() => {
    if (!isOpen) return undefined;

    const closeOnOutsideClick = (event: MouseEvent) => {
      if (event.target instanceof Node && !wrapperRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        toggleRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', closeOnOutsideClick);
    window.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick);
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [isOpen]);

  const selectLanguage = (language: AppLanguage) => {
    setIsOpen(false);
    if (language !== currentLanguage) void i18n.changeLanguage(language);
    toggleRef.current?.focus();
  };

  return (
    <div ref={wrapperRef} className="relative">
      <button
        ref={toggleRef}
        type="button"
        className="inline-flex min-h-10 items-center gap-1.5 rounded px-2 text-secondary transition-colors hover:bg-surface-container-low focus:outline-none focus:ring-2 focus:ring-primary/30 sm:px-3"
        aria-label={`${t('language.switcher_label')}: ${currentLanguageLabel}`}
        aria-haspopup="menu"
        aria-controls="admin-language-menu"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((currentValue) => !currentValue)}
      >
        <span className="material-symbols-outlined text-[20px]" aria-hidden="true">language</span>
        <span className="hidden font-label-sm text-label-sm uppercase sm:inline">
          {currentLanguage}
        </span>
        <span
          className={`material-symbols-outlined hidden text-[16px] transition-transform sm:inline ${isOpen ? 'rotate-180' : ''}`}
          aria-hidden="true"
        >
          expand_more
        </span>
      </button>

      {isOpen && (
        <div
          id="admin-language-menu"
          className="absolute right-0 top-[calc(100%+8px)] z-50 min-w-48 overflow-hidden rounded-lg border border-outline-variant bg-white py-1 shadow-xl"
          role="menu"
          aria-label={t('language.switcher_label')}
        >
          {LANGUAGE_OPTIONS.map((option) => {
            const isActive = currentLanguage === option.code;
            return (
              <button
                key={option.code}
                type="button"
                className={`flex min-h-11 w-full items-center gap-3 px-3 py-2 text-left font-body-sm text-body-sm transition-colors ${isActive ? 'bg-primary-fixed text-primary' : 'text-on-surface hover:bg-surface-container-low'}`}
                role="menuitemradio"
                aria-checked={isActive}
                onClick={() => selectLanguage(option.code)}
              >
                <img
                  src={option.flag}
                  alt=""
                  className="h-4 w-6 rounded-sm border border-outline-variant object-cover"
                />
                <span className="flex-1">{t(option.labelKey)}</span>
                {isActive && (
                  <span className="material-symbols-outlined text-[18px]" aria-hidden="true">check</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminLanguageSwitcher;
